import type {
  OtherIncomeClaimDraft,
  OtherIncomeClaimEntry,
  OtherIncomeEarnerSnapshot,
  YesNo,
} from './otherIncomeEarnerTypes';
import { emptyOtherIncomeEarnerSnapshot } from './otherIncomeEarnerTypes';

export type OtherIncomeClaimEntryApi = {
  id?: number | null;
  empFamilyId?: number | null;
  memberFullName?: string | null;
  memberRelationship?: string | null;
  earnerRole?: string | null;
  earnerRoleOther?: string | null;
  earnerName?: string | null;
  earnerAddress?: string | null;
  memberToEarnerRelationship?: string | null;
  changedDuringYear?: boolean | null;
  changeDate?: string | null;
  changeReason?: string | null;
  changeReasonOther?: string | null;
};

export type OtherIncomeEarnerApi = {
  anyClaimedByOther?: boolean | null;
  entries?: OtherIncomeClaimEntryApi[] | null;
};

function boolToYn(v: boolean | null | undefined): YesNo | '' {
  if (v === true) return 'yes';
  if (v === false) return 'no';
  return '';
}

function ynToBool(v: YesNo | ''): boolean | undefined {
  if (v === 'yes') return true;
  if (v === 'no') return false;
  return undefined;
}

export function mergeEntryIntoSnapshot(
  snap: OtherIncomeEarnerSnapshot,
  draft: OtherIncomeClaimDraft
): OtherIncomeClaimEntry[] {
  if (draft.empFamilyId <= 0) return snap.entries;
  const entry: OtherIncomeClaimEntry = { ...draft };
  return [...snap.entries.filter((e) => e.empFamilyId !== draft.empFamilyId), entry];
}

/** Merge in-progress draft into snap before API persist (per-member incremental save). */
export function otherIncomeEarnerSnapshotForPersist(
  snap: OtherIncomeEarnerSnapshot,
  draft: OtherIncomeClaimDraft
): OtherIncomeEarnerSnapshot {
  if (draft.empFamilyId <= 0 || snap.anyClaimedByOther !== 'yes') return snap;
  return { ...snap, entries: mergeEntryIntoSnapshot(snap, draft) };
}

export function otherIncomeEarnerFromApi(raw: OtherIncomeEarnerApi | null | undefined): OtherIncomeEarnerSnapshot {
  if (!raw) return emptyOtherIncomeEarnerSnapshot();
  const entries: OtherIncomeClaimEntry[] = (raw.entries ?? []).map((e) => ({
    empFamilyId: e.empFamilyId ?? 0,
    memberFullName: e.memberFullName?.trim() ?? '',
    memberRelationship: e.memberRelationship?.trim() ?? '',
    earnerRole: (e.earnerRole?.trim() as OtherIncomeClaimEntry['earnerRole']) ?? '',
    earnerRoleOther: e.earnerRoleOther?.trim() ?? '',
    earnerName: e.earnerName?.trim() ?? '',
    earnerAddress: e.earnerAddress?.trim() ?? '',
    memberToEarnerRelationship:
      (e.memberToEarnerRelationship?.trim() as OtherIncomeClaimEntry['memberToEarnerRelationship']) ?? '',
    changedDuringYear: boolToYn(e.changedDuringYear),
    changeDate: e.changeDate?.trim() ?? '',
    changeReason: (e.changeReason?.trim() as OtherIncomeClaimEntry['changeReason']) ?? '',
    changeReasonOther: e.changeReasonOther?.trim() ?? '',
  }));
  return {
    anyClaimedByOther: boolToYn(raw.anyClaimedByOther),
    entries,
  };
}

export function otherIncomeEarnerToApi(snap: OtherIncomeEarnerSnapshot): OtherIncomeEarnerApi {
  return {
    anyClaimedByOther:
      snap.anyClaimedByOther === 'yes' ? true : snap.anyClaimedByOther === 'no' ? false : null,
    entries: snap.entries.map((e) => ({
      empFamilyId: e.empFamilyId,
      memberFullName: e.memberFullName || undefined,
      memberRelationship: e.memberRelationship || undefined,
      earnerRole: e.earnerRole || undefined,
      earnerRoleOther: e.earnerRole === 'other' ? e.earnerRoleOther.trim() || undefined : undefined,
      earnerName: e.earnerName.trim() || undefined,
      earnerAddress: e.earnerAddress.trim() || undefined,
      memberToEarnerRelationship: e.memberToEarnerRelationship || undefined,
      changedDuringYear: ynToBool(e.changedDuringYear),
      changeDate: e.changedDuringYear === 'yes' ? e.changeDate.trim() || undefined : undefined,
      changeReason: e.changedDuringYear === 'yes' ? e.changeReason || undefined : undefined,
      changeReasonOther:
        e.changedDuringYear === 'yes' && e.changeReason === 'other'
          ? e.changeReasonOther.trim() || undefined
          : undefined,
    })),
  };
}
